import axios from 'axios'
import { toast } from 'react-hot-toast'

// Create axios instance for admin API
export const adminAPI = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/admin',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
adminAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
adminAPI.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'Bir hata oluştu'
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('admin_token')
      window.location.href = '/login'
      return Promise.reject(error)
    }
    
    if (error.response?.status === 403) {
      toast.error('Bu işlem için yetkiniz bulunmuyor')
    } else if (error.response?.status === 429) {
      toast.error('Çok fazla istek. Lütfen bekleyin.')
    } else if (error.response?.status >= 500) {
      toast.error('Sunucu hatası. Lütfen daha sonra tekrar deneyin.')
    } else if (error.code === 'NETWORK_ERROR') {
      toast.error('Ağ bağlantısı hatası')
    } else if (error.code === 'TIMEOUT') {
      toast.error('İstek zaman aşımına uğradı')
    }
    
    return Promise.reject(error)
  }
)

// API endpoints
export const authAPI = {
  login: (credentials) => adminAPI.post('/auth/login', credentials),
  verifyToken: () => adminAPI.get('/auth/verify'),
  refreshToken: () => adminAPI.post('/auth/refresh'),
  logout: () => adminAPI.post('/auth/logout'),
  getProfile: () => adminAPI.get('/auth/profile'),
}

export const dashboardAPI = {
  getDashboardData: () => adminAPI.get('/dashboard'),
  getMetrics: () => adminAPI.get('/dashboard/metrics'),
  getRevenueChart: (days = 30) => adminAPI.get(`/dashboard/revenue-chart?days=${days}`),
  getRecentTransactions: (limit = 10) => adminAPI.get(`/dashboard/recent-transactions?limit=${limit}`),
  getSystemHealth: () => adminAPI.get('/dashboard/system-health'),
}

export const usersAPI = {
  getUsers: (params) => adminAPI.get('/users', { params }),
  getUserDetails: (userId) => adminAPI.get(`/users/${userId}`),
  updateUserStatus: (userId, data) => adminAPI.put(`/users/${userId}/status`, data),
  updateUserCredits: (userId, data) => adminAPI.put(`/users/${userId}/credits`, data),
  addUserNote: (userId, data) => adminAPI.post(`/users/${userId}/notes`, data),
  getUserNotes: (userId, params) => adminAPI.get(`/users/${userId}/notes`, { params }),
  exportUsers: (params) => adminAPI.get('/users/export', { params, responseType: 'blob' }),
}

export const transactionsAPI = {
  getTransactions: (params) => adminAPI.get('/transactions', { params }),
  getTransactionDetails: (transactionId) => adminAPI.get(`/transactions/${transactionId}`),
  exportTransactions: (params) => adminAPI.get('/transactions/export', { params, responseType: 'blob' }),
}

export const rentalsAPI = {
  getRentals: (params) => adminAPI.get('/rentals', { params }),
  getRentalDetails: (rentalId) => adminAPI.get(`/rentals/${rentalId}`),
  exportRentals: (params) => adminAPI.get('/rentals/export', { params, responseType: 'blob' }),
}

export const providersAPI = {
  getProviders: () => adminAPI.get('/providers'),
  toggleProvider: (providerId) => adminAPI.put(`/providers/${providerId}/toggle`),
  syncPrices: () => adminAPI.post('/providers/sync-prices'),
}

export const logsAPI = {
  getLogs: (params) => adminAPI.get('/logs', { params }),
  exportLogs: (params) => adminAPI.get('/logs/export', { params, responseType: 'blob' }),
}

export const settingsAPI = {
  getSettings: () => adminAPI.get('/settings'),
  updateSettings: (data) => adminAPI.put('/settings', data),
  getSystemInfo: () => adminAPI.get('/system-info'),
}

export default adminAPI